/**
 * React Query 전역 캐시 설정 관리
 * 
 * 이 파일에서 모든 캐시 설정을 중앙 관리합니다.
 * 개별 훅에서는 이 설정을 import하여 사용하세요.
 */

// ============================================
// 기본 전역 설정 (대부분의 쿼리에 적용)
// ============================================
export const DEFAULT_CACHE_CONFIG = {
  staleTime: 30 * 60 * 1000, // 30분 - 데이터가 fresh 상태로 유지
  gcTime: 60 * 60 * 1000, // 1시간 - 메모리에 캐시 유지
  refetchOnWindowFocus: false,
  // true = '무효화(stale)된 쿼리만' 마운트 시 다시 불러온다. fresh 한 캐시는 그대로 쓴다.
  // false 였을 때는 invalidateQueries 가 비활성 화면을 stale 로만 표시하고 끝나서
  // 학생 삭제 후 목록으로 돌아와도 새로고침 전까지 옛 데이터가 보였다.
  refetchOnMount: true,
  refetchOnReconnect: 'always' as const,
  retry: 2,
} as const

// ============================================
// 캐시 프리셋 (특수한 경우에만 사용)
// ============================================

/**
 * 정적 데이터용 (거의 변하지 않는 데이터)
 * 예: 학습단계, 교재 목록, 지사 정보
 */
export const STATIC_DATA_CONFIG = {
  staleTime: 60 * 60 * 1000, // 1시간
  gcTime: 4 * 60 * 60 * 1000, // 4시간
  refetchOnWindowFocus: false,
  refetchOnMount: true,
} as const

/**
 * 자주 변경되는 데이터용
 * 예: 출석 현황, 실시간 통계
 */
export const FREQUENT_DATA_CONFIG = {
  staleTime: 1 * 60 * 1000, // 1분
  gcTime: 10 * 60 * 1000, // 10분
  refetchOnWindowFocus: false,
  refetchOnMount: true,
} as const

/**
 * 실시간 데이터용
 * 예: 현재 수업 출석 체크, 라이브 대시보드
 */
export const REALTIME_DATA_CONFIG = {
  staleTime: 0, // 항상 fresh 데이터 요청
  gcTime: 1 * 60 * 1000, // 1분
  refetchInterval: 30 * 1000, // 30초마다 자동 갱신
  refetchOnWindowFocus: false,
  refetchOnMount: true, // 실시간 데이터는 마운트 시 갱신 필요
} as const

/**
 * 사용자 정보용
 * 예: 프로필, 권한 정보
 */
export const USER_DATA_CONFIG = {
  staleTime: 10 * 60 * 1000, // 10분
  gcTime: 30 * 60 * 1000, // 30분
  refetchOnWindowFocus: false,
  refetchOnMount: true,
} as const

// ============================================
// 쿼리별 커스텀 설정 (필요한 경우만)
// ============================================
export const QUERY_SPECIFIC_CONFIG = {
  // 클래스 목록 - 기본 설정 사용
  classes: DEFAULT_CACHE_CONFIG,
  
  // 학생 목록 - 기본 설정 사용
  students: DEFAULT_CACHE_CONFIG,
  
  // 학습 단계 - 정적 데이터
  learningStages: STATIC_DATA_CONFIG,
  
  // 교재 목록 - 정적 데이터
  materials: STATIC_DATA_CONFIG,
  
  // 지사/가맹점 - 정적 데이터
  branches: STATIC_DATA_CONFIG,
  centers: STATIC_DATA_CONFIG,
  
  // 출석 - 자주 변경
  attendance: FREQUENT_DATA_CONFIG,
  
  // 대시보드 - 자주 변경
  dashboard: FREQUENT_DATA_CONFIG,
  
  // 프로필 - 사용자 데이터
  profile: USER_DATA_CONFIG,
  
  // 선생님 목록 - 기본 설정
  teachers: DEFAULT_CACHE_CONFIG,
  
  // 시간표 - 기본 설정
  timetable: DEFAULT_CACHE_CONFIG,
  
  // 상담 - 기본 설정
  consultations: DEFAULT_CACHE_CONFIG,
  
  // 교습비 - 기본 설정
  tuition: DEFAULT_CACHE_CONFIG,
} as const

// ============================================
// 헬퍼 함수
// ============================================

/**
 * 쿼리 키에 따라 적절한 캐시 설정을 반환
 */
export function getCacheConfig(queryKey: string) {
  return QUERY_SPECIFIC_CONFIG[queryKey as keyof typeof QUERY_SPECIFIC_CONFIG] || DEFAULT_CACHE_CONFIG
}

/**
 * 개발 환경에서 캐시 설정 로깅
 */
export function logCacheConfig(queryKey: string) {
  if (process.env.NODE_ENV === 'development') {
    const config = getCacheConfig(queryKey)
    console.log(`📊 Cache Config for "${queryKey}":`, {
      staleTime: `${config.staleTime / 1000 / 60}분`,
      gcTime: `${config.gcTime / 1000 / 60}분`,
    })
  }
}